import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { createApiUrlWithPrefix } from '../config/api';
import { Save, Plus, X, Settings as SettingsIcon, DollarSign, FileText, Shield, Clock, Building2, Image } from 'lucide-react';
import toast from 'react-hot-toast';

const groupIcons = {
  'Rider Payments': DollarSign,
  'Owner Payments': DollarSign,
  'Jeton': SettingsIcon,
  'General': FileText,
  'Insurance': Shield,
  'Session': Clock,
  'Club Branding': Building2,
};

const groupColors = {
  'Rider Payments': 'bg-blue-50 border-blue-200',
  'Owner Payments': 'bg-green-50 border-green-200',
  'Jeton': 'bg-purple-50 border-purple-200',
  'General': 'bg-gray-50 border-gray-200',
  'Insurance': 'bg-yellow-50 border-yellow-200',
  'Session': 'bg-indigo-50 border-indigo-200',
  'Club Branding': 'bg-rose-50 border-rose-200',
};

// Keys that need special rendering (image preview)
const imagePreviewKeys = ['club.logo.url', 'club.favicon.url'];

function AdminSettings() {
  const { user } = useAuth();
  const { refresh: refreshSettings, refreshBranding } = useSettings();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSetting, setNewSetting] = useState({ key: '', value: '', description: '', group: '' });

  // Branding quick-edit form
  const [brandingForm, setBrandingForm] = useState({
    'club.name': '',
    'club.subtitle': '',
    'club.phone': '',
    'club.email': '',
    'club.address': '',
    'club.logo.url': '',
    'theme.primary.color': '#059669',
    'club.footer.note': '',
  });
  const [savingBranding, setSavingBranding] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(createApiUrlWithPrefix('admin/settings'));
      setSettings(res.data);

      // Pre-fill branding form from fetched data
      const map = {};
      res.data.forEach(s => { map[s.key] = s.value || ''; });
      setBrandingForm(prev => ({
        ...prev,
        ...Object.fromEntries(Object.keys(prev).map(k => [k, map[k] ?? prev[k]]))
      }));
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (setting) => {
    setEditingId(setting.id);
    setEditValue(setting.value || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveSetting = async (id) => {
    setSavingId(id);
    try {
      await axios.put(createApiUrlWithPrefix(`admin/settings/${id}`), { value: editValue });
      toast.success('Paramètre mis à jour');
      setSettings(prev => prev.map(s => s.id === id ? { ...s, value: editValue } : s));
      setEditingId(null);
      refreshSettings();
    } catch (err) {
      toast.error('Échec de la mise à jour');
    } finally {
      setSavingId(null);
    }
  };

  const addSetting = async () => {
    if (!newSetting.key.trim()) {
      toast.error('Key is required');
      return;
    }
    try {
      const res = await axios.post(createApiUrlWithPrefix('admin/settings'), newSetting);
      setSettings(prev => [...prev, res.data]);
      setShowAddForm(false);
      setNewSetting({ key: '', value: '', description: '', group: '' });
      toast.success('Paramètre ajouté');
      refreshSettings();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add setting');
    }
  };

  const deleteSetting = async (id) => {
    if (!window.confirm('Supprimer ce paramètre ?')) return;
    try {
      await axios.delete(createApiUrlWithPrefix(`admin/settings/${id}`));
      setSettings(prev => prev.filter(s => s.id !== id));
      toast.success('Paramètre supprimé');
      refreshSettings();
    } catch (err) {
      toast.error('Failed to delete setting');
    }
  };

  const saveBrandingSettings = async () => {
    setSavingBranding(true);
    try {
      // Set favicon url to match logo url so both remain synchronized
      const payload = {
        ...brandingForm,
        'club.favicon.url': brandingForm['club.logo.url']
      };
      await axios.post(createApiUrlWithPrefix('admin/settings/bulk-upsert'), payload);
      toast.success('✅ Paramètres du club sauvegardés et appliqués !');
      refreshSettings();
      refreshBranding();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingBranding(false);
    }
  };

  // Separate branding settings from other settings so they don't duplicate below
  const brandingKeys = ['club.name', 'club.subtitle', 'club.phone', 'club.email', 'club.address', 'club.logo.url', 'club.favicon.url', 'club.footer.note', 'theme.primary.color', 'theme.secondary.color'];
  const otherSettings = settings.filter(s => s.group !== 'Club Branding' && !brandingKeys.includes(s.key));
  const grouped = otherSettings.reduce((acc, s) => {
    const g = s.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(s);
    return acc;
  }, {});

  const groupOrder = ['Rider Payments', 'Owner Payments', 'Jeton', 'General', 'Insurance', 'Session', 'Other'];

  if (user?.role !== 4 && user?.role !== 'SuperAdmin') {
    return <div className="p-8 text-center text-red-600 font-semibold">Accès refusé. SuperAdmin seulement.</div>;
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const presetThemes = [
    { name: 'Émeraude (Vert)', primary: '#059669', secondary: '#3b82f6' },
    { name: 'Royal (Bleu)', primary: '#2563eb', secondary: '#059669' },
    { name: 'Pourpre (Violet)', primary: '#7c3aed', secondary: '#ec4899' },
    { name: 'Bordeaux (Rouge)', primary: '#dc2626', secondary: '#f59e0b' },
    { name: 'Ambre (Orange)', primary: '#d97706', secondary: '#2563eb' },
    { name: 'Sombre (Obsidienne)', primary: '#1e293b', secondary: '#6366f1' },
  ];

  const brandingFields = [
    { key: 'club.name', label: 'Nom du Club / Établissement', placeholder: 'Club Équestre Le Galop', type: 'text' },
    { key: 'club.subtitle', label: 'Sous-titre', placeholder: 'Centre Équestre & Élevage', type: 'text' },
    { key: 'club.phone', label: 'Téléphone', placeholder: '0550 12 34 56', type: 'text' },
    { key: 'club.email', label: 'Email', placeholder: 'contact@club.dz', type: 'email' },
    { key: 'club.address', label: 'Adresse Physique', placeholder: 'Route Nationale, Alger', type: 'text' },
    { key: 'club.logo.url', label: 'Logo & Favicon (Téléchargez une image ou collez une URL)', placeholder: 'Télécharger une image ou entrer une URL...', type: 'file', preview: true },
    { key: 'theme.primary.color', label: 'Couleur Principale (Boutons, En-tête, Accent)', placeholder: '#059669', type: 'color' },
    { key: 'theme.secondary.color', label: 'Couleur Secondaire', placeholder: '#3b82f6', type: 'color' },
    { key: 'club.footer.note', label: 'Note de bas de facture', placeholder: 'Merci pour votre confiance !', type: 'text' },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paramètres & Configuration</h1>
          <p className="text-sm text-gray-500 mt-1">Tarifs, identité visuelle et données du club</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showAddForm ? 'Annuler' : 'Ajouter un paramètre'}
        </button>
      </div>

      {/* ── Club Branding Section (DB-backed) ─────────────────────────────── */}
      <div className="mb-8 rounded-xl border-2 border-rose-200 bg-rose-50 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-rose-200 bg-white/70">
          <Building2 className="h-5 w-5 text-rose-600" />
          <div>
            <h2 className="text-lg font-bold text-gray-800">🏇 Identité & Branding du Club</h2>
            <p className="text-xs text-gray-500">Ces données sont stockées en base de données et appliquées à l'application, au favicon et aux factures imprimées</p>
          </div>
        </div>
        <div className="p-5 border-b border-rose-100 bg-white/40">
          <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">
            Thèmes Prédéfinis Rapides (One-Click Palettes)
          </label>
          <div className="flex flex-wrap gap-2">
            {presetThemes.map(theme => (
              <button
                key={theme.name}
                type="button"
                onClick={() => setBrandingForm(prev => ({
                  ...prev,
                  'theme.primary.color': theme.primary,
                  'theme.secondary.color': theme.secondary
                }))}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-xs font-medium text-gray-700 hover:border-gray-400 transition-all shadow-sm"
              >
                <span className="w-3.5 h-3.5 rounded-full border" style={{ backgroundColor: theme.primary }} />
                <span>{theme.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {brandingFields.map(field => (
            <div key={field.key}>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-1.5">
                {field.label}
              </label>
              {field.type === 'file' ? (
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error('Image file must be under 2MB');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setBrandingForm(prev => ({ ...prev, [field.key]: reader.result }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="w-full text-xs text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-rose-100 file:text-rose-700 hover:file:bg-rose-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={brandingForm[field.key] || ''}
                    onChange={e => setBrandingForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder="Ou entrez une URL / Base64..."
                    className="w-full px-3 py-1.5 border border-rose-200 bg-white rounded-lg text-xs outline-none focus:border-rose-400 text-gray-500"
                  />
                </div>
              ) : (
                <input
                  type={field.type}
                  value={brandingForm[field.key] || ''}
                  onChange={e => setBrandingForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  className={`w-full px-3 ${field.type === 'color' ? 'h-10 cursor-pointer p-1' : 'py-2'} border border-rose-200 bg-white rounded-lg text-sm outline-none focus:border-rose-400 focus:ring-1 focus:ring-rose-200 transition-all`}
                />
              )}
              {field.preview && brandingForm[field.key]?.trim() && (
                <div className="mt-2 flex items-center gap-2">
                  <img
                    src={brandingForm[field.key]}
                    alt={field.label}
                    className="h-12 max-w-[160px] object-contain rounded border border-rose-200 bg-white p-1"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setBrandingForm(prev => ({ ...prev, [field.key]: '' }))}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Supprimer l'image
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="px-5 py-4 bg-white/50 border-t border-rose-200 flex justify-end">
          <button
            onClick={saveBrandingSettings}
            disabled={savingBranding}
            className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-60 font-semibold transition-colors"
          >
            <Save className="h-4 w-4" />
            {savingBranding ? 'Sauvegarde...' : 'Sauvegarder le Branding'}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="mb-8 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Nouveau paramètre</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Clé (ex: lesson.extra.price)"
              value={newSetting.key}
              onChange={e => setNewSetting({ ...newSetting, key: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Valeur"
              value={newSetting.value}
              onChange={e => setNewSetting({ ...newSetting, value: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Description"
              value={newSetting.description}
              onChange={e => setNewSetting({ ...newSetting, description: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="Groupe (ex: Rider Payments)"
              value={newSetting.group}
              onChange={e => setNewSetting({ ...newSetting, group: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <button
            onClick={addSetting}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
          >
            Ajouter
          </button>
        </div>
      )}

      {/* ── Pricing & Other Settings ─────────────────────────────────────── */}
      <div className="space-y-6">
        {groupOrder.filter(g => grouped[g]).map(group => {
          const Icon = groupIcons[group] || SettingsIcon;
          const colorClass = groupColors[group] || 'bg-gray-50 border-gray-200';
          return (
            <div key={group} className={`rounded-xl border ${colorClass} overflow-hidden`}>
              <div className="flex items-center gap-3 px-5 py-3 border-b border-inherit bg-white/60">
                <Icon className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-800">{group}</h2>
                <span className="text-xs text-gray-400 ml-auto">{grouped[group].length} paramètre(s)</span>
              </div>
              <div className="divide-y divide-inherit">
                {grouped[group].map(setting => (
                  <div key={setting.id} className="px-5 py-3 flex items-center gap-4 hover:bg-white/40 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-gray-400 truncate">{setting.key}</div>
                      {setting.description && (
                        <div className="text-xs text-gray-500 mt-0.5">{setting.description}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {editingId === setting.id ? (
                        <>
                          <input
                            type="text"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            className="w-32 px-3 py-1.5 border-2 border-primary-400 rounded-lg text-sm font-semibold text-right bg-white"
                            autoFocus
                            onKeyDown={e => {
                              if (e.key === 'Enter') saveSetting(setting.id);
                              if (e.key === 'Escape') cancelEdit();
                            }}
                          />
                          <button
                            onClick={() => saveSetting(setting.id)}
                            disabled={savingId === setting.id}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          >
                            <Save className="h-4 w-4" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span className="text-lg font-bold text-gray-900 min-w-[80px] text-right">
                            {setting.value || <span className="text-gray-300 font-normal text-sm">—</span>}
                          </span>
                          <button
                            onClick={() => startEdit(setting)}
                            className="px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors border border-primary-200"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => deleteSetting(setting.id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {settings.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <SettingsIcon className="h-12 w-12 mx-auto mb-4 opacity-40" />
          <p>Aucun paramètre configuré. Ajoutez-en un avec le bouton ci-dessus.</p>
        </div>
      )}
    </div>
  );
}

export default AdminSettings;
